import React, { useState, useRef } from 'react';
import { Box, HStack } from '@chakra-ui/react';
import { HiMicrophone, HiArrowUp } from 'react-icons/hi2';
import { useTranslation } from 'react-i18next';
import AttachmentMenu from './AttachmentMenu';
import useVoiceInput from '../hooks/useVoiceInput';

const InputArea = ({ onSendMessage, isSending, colors, onFileSelect, disabled = false, onFocus }) => {
    const { t } = useTranslation('chat');
    const [text, setText] = useState('');
    const textareaRef = useRef(null);
    const { isRecording, isSupported, startRecording, stopRecording } = useVoiceInput();

    const handleTranscript = (transcript) => {
        setText(prev => prev ? prev + ' ' + transcript : transcript);
    };

    const handleMicClick = () => {
        if (isRecording) stopRecording();
        else startRecording(handleTranscript);
    };

    const hasContent = text.trim().length > 0;

    const handleSend = () => {
        if (!hasContent || isSending || disabled) return;
        onSendMessage({ type: 'text', text: text.trim() });
        setText('');
        if (textareaRef.current) {
            textareaRef.current.style.height = 'auto';
        }
    };

    const handleInput = (e) => {
        setText(e.target.value);
        const el = e.target;
        el.style.height = 'auto';
        el.style.height = Math.min(el.scrollHeight, 160) + 'px';
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey && window.matchMedia('(hover: hover)').matches) {
            e.preventDefault();
            handleSend();
        }
    };

    const sendActive = hasContent && !isSending && !disabled;

    return (
        <Box
            position="sticky"
            bottom={0}
            bg={colors.inputAreaBg}
            borderTopWidth="1px"
            borderTopColor={colors.borderColor}
            px={3}
            py={2}
        >
            <Box
                bg={colors.inputBg}
                borderRadius="20px"
                px={3}
                pt={3}
                pb={2}
                opacity={disabled ? 0.6 : 1}
            >
                <textarea
                    ref={textareaRef}
                    value={text}
                    onChange={handleInput}
                    onKeyDown={handleKeyDown}
                    onFocus={onFocus}
                    placeholder={t('inputPlaceholder')}
                    rows={2}
                    disabled={disabled}
                    style={{
                        width: '100%',
                        maxWidth: '100%',
                        boxSizing: 'border-box',
                        background: 'transparent',
                        color: colors.textPrimary,
                        border: 'none',
                        outline: 'none',
                        fontSize: '16px',
                        lineHeight: '1.5',
                        resize: 'none',
                        overflowY: 'auto',
                        maxHeight: '160px',
                        fontFamily: 'Inter, sans-serif',
                        display: 'block',
                        cursor: disabled ? 'not-allowed' : 'text',
                    }}
                />

                <HStack justifyContent="space-between" mt={2} alignItems="center">
                    <AttachmentMenu onFileSelect={onFileSelect} colors={colors} isSending={isSending || disabled} />

                    <HStack gap={1} alignItems="center">
                        <Box
                            as="button"
                            onClick={handleMicClick}
                            disabled={!isSupported || disabled}
                            p={2}
                            borderRadius="full"
                            color={isRecording ? '#E53E3E' : '#A424F4'}
                            opacity={(!isSupported || disabled) ? 0.4 : 1}
                            cursor={(!isSupported || disabled) ? 'not-allowed' : 'pointer'}
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            transition="background 0.15s"
                            _hover={isSupported ? { bg: colors.inputAreaBg } : {}}
                            flexShrink={0}
                            aria-label={isRecording ? 'Zatrzymaj nagrywanie' : 'Mikrofon'}
                        >
                            <HiMicrophone size={20} />
                        </Box>

                        <Box
                            as="button"
                            onClick={handleSend}
                            disabled={!sendActive}
                            w="26px"
                            h="26px"
                            borderRadius="full"
                            bg={sendActive ? '#A424F4' : 'rgba(164,36,244,0.25)'}
                            color="white"
                            cursor={sendActive ? 'pointer' : 'not-allowed'}
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            flexShrink={0}
                            transition="background 0.15s"
                            aria-label="Wyślij"
                        >
                            <HiArrowUp size={14} />
                        </Box>
                    </HStack>
                </HStack>
            </Box>
        </Box>
    );
};

export default InputArea;
